import React, { useState, useEffect } from 'react';
import axios from 'axios';
import useFuelData from '../hooks/useFuelData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

/**
 * FuelNearbyCard — Live fuel price card + alert setup
 * Shows cheapest fuel station near user with savings vs average.
 *
 * Phase 1 (audit doc 11.06.2026): localStorage TTL cache + timeout + retries.
 * Phase 2 (audit doc 11.06.2026): consumes the shared `useFuelData` hook so
 *   it inherits stale-while-revalidate, exponential backoff, and session
 *   retry budget for free.
 */
const FuelNearbyCard = ({ onViewAll, userId }) => {
  const [coords, setCoords] = useState(null);
  const [locationDenied, setLocationDenied] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertPrice, setAlertPrice] = useState('');
  const [alertSaving, setAlertSaving] = useState(false);
  const [alertDone, setAlertDone] = useState(false);

  // Shared hook: averages (used for vs-average savings strap-line)
  const avgState = useFuelData(
    '/api/fuel/prices/live',
    {},
    { cachePrefix: 'averages-home-card', fetchOptions: { credentials: 'include' } }
  );

  // Shared hook: nearest station — only enabled when we have coords.
  const stationParams = coords
    ? {
        lat: coords.lat.toFixed(4),
        lng: coords.lng.toFixed(4),
        radius_miles: 3,
        limit: 1,
        fuel_type: 'unleaded',
      }
    : null;
  const stationState = useFuelData(
    '/api/fuel/stations/live',
    stationParams,
    {
      cachePrefix: 'nearest-home-card',
      enabled: !!coords,
      fetchOptions: { credentials: 'include' },
    }
  );

  // Resolve geolocation once on mount.
  useEffect(() => {
    if (!navigator.geolocation) {
      setLocationDenied(true);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => setLocationDenied(true),
      { timeout: 5000 }
    );
  }, []);

  const handleCreateAlert = async () => {
    if (!userId || !alertPrice) return;
    setAlertSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/fuel/alerts`, {
        user_id: userId,
        fuel_type: 'unleaded',
        price_threshold: parseFloat(alertPrice),
        radius_km: 10,
      });
      setAlertDone(true);
      setTimeout(() => { setShowAlert(false); setAlertDone(false); }, 2000);
    } catch (e) {
      console.error('Fuel alert error:', e);
    }
    setAlertSaving(false);
  };

  // Initial loading skeleton — only while we have NO data and NO denial yet.
  const stillResolvingLocation = !coords && !locationDenied;
  const initialLoading =
    (stillResolvingLocation) ||
    (avgState.loading && !avgState.data) ||
    (!!coords && stationState.loading && !stationState.data);

  if (initialLoading) {
    return (
      <div
        className="animate-pulse bg-white rounded-xl p-4 border border-gray-100"
        data-testid="fuel-nearby-skeleton"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 flex-1">
            <div className="w-10 h-10 rounded-lg bg-gray-200 flex-shrink-0" />
            <div className="flex-1 space-y-2">
              <div className="h-3 bg-gray-200 rounded w-1/2" />
              <div className="h-2.5 bg-gray-100 rounded w-1/3" />
            </div>
          </div>
          <div className="space-y-2">
            <div className="h-5 bg-gray-200 rounded w-14" />
            <div className="h-2 bg-gray-100 rounded w-10 ml-auto" />
          </div>
        </div>
      </div>
    );
  }

  const avgPrice = avgState.data?.fuel_types?.unleaded?.price;
  const station = stationState.data?.stations?.[0] || null;
  const displayPrice = station?.fuel_types?.unleaded?.price || avgPrice;
  const distanceKm = station?.distance_km;
  const distanceMiles = distanceKm ? (distanceKm * 0.621371).toFixed(1) : null;
  const savingsPerLitre =
    avgPrice && station?.fuel_types?.unleaded?.price && avgPrice > station.fuel_types.unleaded.price
      ? ((avgPrice - station.fuel_types.unleaded.price) / 100).toFixed(2)
      : null;
  const hasLocation = !locationDenied && station;
  const showStaleHint = avgState.stale || stationState.stale;

  return (
    <div
      className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-all"
      data-testid={hasLocation ? 'fuel-card-live' : 'fuel-card-avg'}
    >
      <button
        onClick={onViewAll}
        className="w-full p-4 text-left hover:bg-gray-50 transition-all active:scale-[0.99]"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center text-lg">⛽</div>
            <div>
              <p className="text-sm font-semibold text-gray-700">Cheapest Fuel Near You</p>
              <p className="text-[11px] text-gray-400">
                {hasLocation
                  ? `${station.brand || station.name}${distanceMiles ? ` · ${distanceMiles} mi` : ''}`
                  : 'Enable location for nearby prices'}
              </p>
            </div>
          </div>
          <div className="text-right">
            {displayPrice && (
              <p className="text-lg font-bold text-green-600">
                {displayPrice}p
                {!hasLocation && <span className="text-[10px] font-normal text-gray-400 ml-0.5">avg</span>}
              </p>
            )}
            <p className="text-[10px] text-green-600 font-medium">View stations</p>
          </div>
        </div>
        {savingsPerLitre && (
          <p className="text-[11px] text-green-600 font-medium mt-1 pl-[52px]" data-testid="fuel-savings">
            You save ~£{savingsPerLitre}/litre vs average
          </p>
        )}
        {showStaleHint && (
          <p
            className="text-[10px] text-amber-700 font-medium mt-1 pl-[52px]"
            data-testid="fuel-nearby-stale-hint"
          >
            Showing previously saved price — couldn’t reach the live feed.
          </p>
        )}
      </button>

      {/* Alert toggle */}
      {userId && (
        <div className="border-t border-gray-50 px-4 py-2">
          {!showAlert ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowAlert(true);
                setAlertPrice(avgPrice ? String(Math.floor(avgPrice - 2)) : '140');
              }}
              className="text-[11px] text-gray-400 hover:text-green-600 font-medium transition"
              data-testid="fuel-alert-toggle"
            >
              Set price alert
            </button>
          ) : alertDone ? (
            <p className="text-[11px] text-green-600 font-medium" data-testid="fuel-alert-done">Alert set</p>
          ) : (
            <div className="flex items-center gap-2" data-testid="fuel-alert-form">
              <span className="text-[11px] text-gray-500">Notify below</span>
              <input
                type="number"
                value={alertPrice}
                onChange={(e) => setAlertPrice(e.target.value)}
                className="w-16 text-xs border border-gray-200 rounded-md px-2 py-1 text-center focus:border-green-400 focus:outline-none"
                placeholder="140"
              />
              <span className="text-[11px] text-gray-400">p</span>
              <button
                onClick={handleCreateAlert}
                disabled={alertSaving || !alertPrice}
                className="text-[11px] font-semibold text-white bg-green-600 hover:bg-green-700 px-2.5 py-1 rounded-md transition disabled:opacity-50"
              >
                {alertSaving ? '...' : 'Set'}
              </button>
              <button
                onClick={() => setShowAlert(false)}
                className="text-[11px] text-gray-400 hover:text-gray-600"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FuelNearbyCard;
